/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-16 06:24:30
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { downloadFile } from 'src/helpers';

import apiService from 'src/services/api-service';
import {
  GET_SUGGESTIONS_REQUEST,
  GET_SUGGESTIONS_SUCCESS,
  GET_SUGGESTIONS_FAILURE,
  BULK_SUGGESTIONS_REQUEST,
  BULK_SUGGESTIONS_SUCCESS,
  BULK_SUGGESTIONS_FAILURE,
  DELETE_SUGGESTIONS_REQUEST,
  DELETE_SUGGESTIONS_SUCCESS,
  DELETE_SUGGESTIONS_FAILURE,
  UPDATE_SUGGESTION_REQUEST,
  UPDATE_SUGGESTION_SUCCESS,
  UPDATE_SUGGESTION_FAILURE,
  VERIFY_CONTENT_BEFORE_IMPORT_REQUEST,
  VERIFY_CONTENT_BEFORE_IMPORT_SUCCESS,
  VERIFY_CONTENT_BEFORE_IMPORT_FAILURE,
  IMPORT_FILE_REQUEST,
  IMPORT_FILE_SUCCESS,
  IMPORT_FILE_FAILURE,
  EXPORT_FILE_REQUEST,
  EXPORT_FILE_SUCCESS,
  EXPORT_FILE_FAILURE
} from 'stores/flow-type/suggestion-dictionary.type';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get suggestions
 * @return {object} The RSAA object for API middleware
 */
export const getSuggestions = (q, skip, limit, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.suggestion-dictionary.get-suggestions',
    { queries: { q, skip, limit } },
    [
      GET_SUGGESTIONS_REQUEST,
      {
        type: GET_SUGGESTIONS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_SUGGESTIONS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SuggestionDictionary.Actions.getSuggestions'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to bulk suggestions
 * @return {object} The RSAA object for API middleware
 */
export const bulkSuggestions = (suggestions, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.suggestion-dictionary.bulk-suggestions',
    { body: suggestions },
    [
      BULK_SUGGESTIONS_REQUEST,
      {
        type: BULK_SUGGESTIONS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'info',
            i18n.translate('AdminHTML.SuggestionDictionary.Actions.bulkSuggestions_Success'),
            i18n
          );

          // Track Full Story event
          trackFSEvent('Add Suggestion Dictionary', {
            feature: 'Suggestion Dictionary',
            noSuggestion: suggestions?.length
          });

          return { data };
        }
      },
      {
        type: BULK_SUGGESTIONS_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SuggestionDictionary.Actions.bulkSuggestions_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to delete suggestions
 * @return {object} The RSAA object for API middleware
 */
export const deleteSuggestions = (suggestionIds, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.suggestion-dictionary.delete-suggestions',
    { queries: { suggestionIds } },
    [
      DELETE_SUGGESTIONS_REQUEST,
      {
        type: DELETE_SUGGESTIONS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom',
            i18n.translate('AdminHTML.SuggestionDictionary.Actions.deleteSuggestions_Success'),
            i18n
          );

          // Track Full Story event
          trackFSEvent('Delete Suggestion Dictionary', {
            feature: 'Suggestion Dictionary',
            noSuggestion: suggestionIds?.length
          });

          return { data };
        }
      },
      {
        type: DELETE_SUGGESTIONS_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SuggestionDictionary.Actions.deleteSuggestions_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to update suggestion
 * @return {object} The RSAA object for API middleware
 */
export const updateSuggestion = (suggestions, actionName = '', i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.suggestion-dictionary.update-suggestion',
    { body: suggestions },
    [
      UPDATE_SUGGESTION_REQUEST,
      {
        type: UPDATE_SUGGESTION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom',
            actionName === 'changeStatus'
              ? i18n.translate(
                  'AdminHTML.SuggestionDictionary.Actions.updateSuggestion_Success_ChangeStatus'
                )
              : i18n.translate(
                  'AdminHTML.SuggestionDictionary.Actions.updateSuggestion_Success_NotChangeStatus'
                ),
            i18n
          );

          if (actionName === 'changeStatus') {
            const PRIORITY_LEVEL = {
              1: 'low',
              2: 'normal',
              3: 'high'
            };
            if (suggestions[0]?.searchEnabled) {
              // Track Full Story event
              trackFSEvent('Turn on Suggestion Dictionary', {
                feature: 'Suggestion Dictionary',
                priority: PRIORITY_LEVEL[suggestions[0]?.priority]
              });
            } else {
              // Track Full Story event
              trackFSEvent('Turn off Suggestion Dictionary', {
                feature: 'Suggestion Dictionary',
                priority: PRIORITY_LEVEL[suggestions[0]?.priority]
              });
            }
          }

          return { data, actionName };
        }
      },
      {
        type: UPDATE_SUGGESTION_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            actionName === 'changeStatus'
              ? i18n.translate(
                  'AdminHTML.SuggestionDictionary.Actions.updateSuggestion_Failure_ChangeStatus'
                )
              : i18n.translate(
                  'AdminHTML.SuggestionDictionary.Actions.updateSuggestion_Failure_NotChangeStatus'
                ),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to verify file content before import
 * @return {object} The RSAA object for API middleware
 */
export const verifyFileContent = files => {
  return apiService.sendRequest(
    'admin-v3.suggestion-dictionary.verify-import-suggestions',
    { attachements: files },
    [
      VERIFY_CONTENT_BEFORE_IMPORT_REQUEST,
      {
        type: VERIFY_CONTENT_BEFORE_IMPORT_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: VERIFY_CONTENT_BEFORE_IMPORT_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { ...data };
        }
      }
    ]
  );
};

/**
 * call to import file content
 * @return {object} The RSAA object for API middleware
 */
export const importFile = (files, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.suggestion-dictionary.import-suggestions',
    { attachements: files },
    [
      IMPORT_FILE_REQUEST,
      {
        type: IMPORT_FILE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          // Track Full Story event
          trackFSEvent('Import Suggestion Dictionary', {
            feature: 'Suggestion Dictionary'
          });

          return { data };
        }
      },
      {
        type: IMPORT_FILE_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SuggestionDictionary.Actions.importFile'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to download file content
 * @return {object} The RSAA object for API middleware
 */
export const exportFile = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.suggestion-dictionary.export-suggestions', {}, [
    EXPORT_FILE_REQUEST,
    {
      type: EXPORT_FILE_SUCCESS,
      payload: async (action, state, res) => {
        // Convert response data into blob and download
        const blob = await res.blob();
        downloadFile(blob, 'All suggestion dictionary data.csv');

        const data = await getJSON(res);

        // Track Full Story event
        trackFSEvent('Export Suggestion Dictionary', {
          feature: 'Suggestion Dictionary'
        });

        return { data };
      }
    },
    {
      type: EXPORT_FILE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SuggestionDictionary.Actions.exportFile'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};
